/**
 * DonationCaptureDonorStep.tsx - Donor Information Collection Step
 *
 * This component handles the second step of donation capture where donor
 * contact information is collected with validation and formatting.
 *
 * Key Features:
 * - Donor contact information form
 * - Real-time validation and formatting
 * - Anonymous donation option
 * - Phone number formatting by country
 * - Email validation with error feedback
 * - Optional comment/message field
 *
 * Form Fields:
 * - First Name and Last Name (required)
 * - Email Address (required, validated)
 * - Phone Number (optional, formatted)
 * - Anonymous Donation Checkbox
 * - Donation Comment/Message (optional)
 *
 * Validation:
 * - Required field validation
 * - Email format validation
 * - Phone number format validation
 * - Real-time error feedback
 *
 * Props:
 * - donorInfo: DonorInfo - Current donor information
 * - onDonorInfoChange: (info: DonorInfo) => void - Info change handler
 * - onNext: () => void - Proceed to next step handler
 * - onBack: () => void - Return to previous step handler
 *
 * Dependencies: Form validation utilities, phone/email formatters
 */

import React, { useState, useEffect } from "react";
import styled from "styled-components";
import {
  CountryDropdown,
  CountryWithStateModel,
  CountryPhoneDropdown,
  ApplicationSettingsStore,
} from "@mindmemobile/mm-payment-modules";
import {
  CoreInputModelUtil,
  InputErrorModel,
  SingleSelect,
} from "@mindmemobile/mm-shared-global";
import { DonorInfo } from "../../models";

const DonorFormContainer = styled.div`
  display: flex;
  flex-direction: column;
  gap: 0;
`;

const DonorFormRow = styled.div`
  display: grid;
  gap: 16px;
  margin-bottom: 20px;
`;

const NameRow = styled(DonorFormRow)`
  grid-template-columns: 1fr 1fr;
`;

const ZipCityRow = styled(DonorFormRow)`
  grid-template-columns: 0.7fr 1.3fr;
`;

export const StateCountryRow = styled(DonorFormRow)`
  grid-template-columns: 0.7fr 1.3fr;
  .single-select-donor-input {
    height: 58px;
    border: 2px solid #e2e8f0;
    border-radius: 12px;
    color: #a9afb9;
    background-color: #ffffff;
    padding-left: 0px;
    .custom-label {
      color: #a9afb9;
    }
    .header {
      height: 56px;
      border: none;
      .select-header-span {
        line-height: 43px;
        color: #777;
      }
      .select-header-icon {
        height: 50px;
      }
    }
    &.donation-capture-select {
      width: 240px;
      .header {
        width: 240px;
      }
      .select-header-icon {
        border-left: none;
        .fa.fa-solid.fa-caret-down {
          color: #aaa;
        }
      }
      .select-header-span {
        font-size: 18px;
        color: #1e293b;
        &::placeholder {
          color: #9ca3af;
        }
      }
      .dropdown-option-list {
        li {
          font-size: 18px;
          color: #1e293b;
        }
      }
      .options-menu {
        border-radius: 4px;
        transform: translate(0px, 60px);
        .input-wrapper {
          border-radius: 12px;
          input.searchable-dropdown-input {
            border-radius: 12px 12px 0px 0px;
          }
        }
      }
    }
  }
`;

const FormGroup = styled.div`
  display: flex;
  flex-direction: column;
  &.donation-country-dropdown {
    .country-phone-wrapper {
      height: 100%;
    }
    .country-dropdown-expanded {
      height: 100%;
      > div {
        height: 58px;
        border: 2px solid #e2e8f0;
        border-radius: 12px;
        color: #1e293b;
        padding-left: 12px;
        .custom-label {
          color: #1e293b;
        }
      }
      .custom-label {
        font-size: 18px;
      }
    }
    .dropdown-menu {
      max-height: 182px;
      transform: translate(-11px, 44px) !important;
      a {
        font-size: 18px;
        color: #1e293b;
      }
    }
  }
`;

const FullWidthFormGroup = styled.div`
  display: flex;
  flex-direction: column;
  margin-bottom: 20px;
  &.donation-country-dropdown {
    .ps-country-dpdn {
      .input-wrapper {
        width: 100%;
        font-size: 18px;
        input{
          font-size: 18px;
          color: #1e293b;
        }
      }
      & > div {
        height: 58px;
        border: 2px solid #e2e8f0;
        border-radius: 12px;
        color: #1e293b;
        padding-left: 12px;
        width: 100%;

        .custom-label {
          color: #1e293b;
          font-size: 18px;
        }
      }
      .dropdown-menu {
        min-width: 470px;
        max-height: 182px;
        transform: translate(-11px, 44px) !important;
        a {
          font-size: 18px;
          color: #1e293b;
        }
      }
    }
  }
`;
interface InputProps {
  hasError?: boolean;
}

const FormInput = styled.input<InputProps>`
  padding: 14px 16px;
  border: 2px solid #e2e8f0;
  border-radius: 12px;
  font-size: 18px;
  color: #1e293b;
  transition: border-color 0.2s ease;
  background: #ffffff;
  border-color: ${({ hasError }) =>
    hasError ? "#ef4444" : "#e2e8f0"}; // red if error

  &:focus {
    outline: none;
    border-color: #10b981;
    box-shadow: 0 0 0 3px rgba(16, 185, 129, 0.1);
  }

  &::placeholder {
    color: #9ca3af;
    font-size: 18px;
  }
`;

const PhoneInputContainer = styled.div`
  position: relative;
  display: flex;
  align-items: center;
`;

const PhonePrefix = styled.div`
  position: absolute;
  left: 16px;
  top: 50%;
  transform: translateY(-50%);
  font-size: 18px;
  font-weight: 600;
  color: #64748b;
  z-index: 1;
`;

const PhoneInput = styled.input<{ hasError: boolean }>`
  padding: 14px 16px 14px 48px;
  border: 2px solid ${(props) => (props.hasError ? "#dc2626" : "#e2e8f0")};
  border-radius: 12px;
  font-size: 18px;
  color: #1e293b;
  transition: border-color 0.2s ease;
  background: #ffffff;
  width: 100%;

  &:focus {
    outline: none;
    border-color: ${(props) => (props.hasError ? "#dc2626" : "#10b981")};
    box-shadow: 0 0 0 3px
      ${(props) =>
    props.hasError ? "rgba(220, 38, 38, 0.1)" : "rgba(16, 185, 129, 0.1)"};
  }

  &::placeholder {
    color: #9ca3af;
    font-size: 18px;
  }
`;

const CountrySelect = styled.select`
  padding: 14px 16px;
  border: 2px solid #e2e8f0;
  border-radius: 12px;
  font-size: 18px;
  color: #1e293b;
  background: #f8fafc;
  cursor: not-allowed;
  appearance: none;
  opacity: 0.7;
`;

const CheckboxContainer = styled.div`
  display: grid;
  grid-template-columns: 1fr 1fr;
  gap: 16px;
  margin: 8px 0;
`;

const CheckboxItem = styled.label`
  display: flex;
  align-items: flex-start;
  gap: 12px;
  cursor: pointer;
  font-size: 18px;
  color: #1e293b;
  line-height: 1.4;
`;

const Checkbox = styled.input<{ checked?: boolean }>`
  width: 20px;
  height: 20px;
  border-radius: 4px;
  border: 2px solid ${(props) => (props.checked ? "#10b981" : "#e2e8f0")};
  background: ${(props) => (props.checked ? "#10b981" : "#ffffff")};
  appearance: none;
  margin: 0;
  flex-shrink: 0;
  margin-top: 2px;
  cursor: pointer;
  transition: all 0.2s ease;
  position: relative;

  &:checked::after {
    content: "✓";
    position: absolute;
    top: 50%;
    left: 50%;
    transform: translate(-50%, -50%);
    color: #ffffff;
    font-size: 14px;
    font-weight: 700;
  }

  &:hover {
    border-color: #10b981;
  }
`;

const DisclaimerText = styled.div`
  font-size: 12px;
  color: #64748b;
  line-height: 1.5;
  margin-left: 32px;
  margin-top: 4px;
  margin-bottom: 24px;
`;

const LineBreakSpacer = styled.div`
  height: 48px;
`;

interface DonationCaptureDonorStepProps {
  initialData: DonorInfo;
  onDataChange: (data: DonorInfo) => void;
  onValidationChange: (isValid: boolean) => void;
  countriesWithState: CountryWithStateModel[];
}

export const DonationCaptureDonorStep: React.FC<
  DonationCaptureDonorStepProps
> = ({ initialData, onDataChange, onValidationChange, countriesWithState }) => {
  const [donorInfo, setDonorInfo] = useState<DonorInfo>(
    new DonorInfo({
      ...initialData,
      country:
        initialData.country ||
        countriesWithState.find((c) => c.getUSCountry())?.id ||
        "",
      phoneCountryId:
        initialData.phoneCountryId ||
        countriesWithState.find((c) => c.getUSCountry())?.id ||
        "",
      countryDetails: initialData.countryDetails?.id
        ? initialData.countryDetails
        : countriesWithState.find((c) => c.getUSCountry()),
    })
  );
  const [phoneError, setPhoneError] = useState(false);

  // Update parent when data changes
  useEffect(() => {
    onDataChange(donorInfo);
  }, [donorInfo, onDataChange]);

  // Update validation when relevant data changes
  useEffect(() => {
    const isValid = isStepValid();
    onValidationChange(isValid);
  }, [donorInfo, phoneError, onValidationChange]);

  const updateDonorInfo = (
    field: keyof DonorInfo,
    value: string | boolean | any
  ) => {
    setDonorInfo((prev) => {
      const updatedInfo = new DonorInfo({
        ...prev,
        [field]: value,
      });
      updatedInfo.validate(true);
      switch (field) {
        case "zip":
          /* Key comes from the Payment API ApplicationSettings (GoogleMapsAPIKey); it used to be a
             literal in mm-shared-global (security finding F03b). No key configured = no lookup, the
             donor just fills state/country by hand. */
          const googleMapsAPIKey = ApplicationSettingsStore.getGoogleMapsAPIKey();
          if (
            typeof value === "string" &&
            value.length >= 4 &&
            googleMapsAPIKey
          ) {
            fetch(
              `https://maps.googleapis.com/maps/api/geocode/json?address=${encodeURIComponent(value)}&key=${encodeURIComponent(googleMapsAPIKey)}`
            )
              .then((res) => res.json())
              .then((data) => {
                if (data.status === "OK") {
                  const components = data.results[0].address_components;
                  const state =
                    components.find((c: any) =>
                      c.types.includes("administrative_area_level_1")
                    )?.long_name || "";

                  const country =
                    components.find((c: any) =>
                      c.types.includes("country")
                    )?.long_name || "";

                  const city =
                    components.find((c: any) =>
                      c.types.includes("locality")
                    )?.long_name || "";

                  const countryDetails = countriesWithState.find((c) => c.name.toLowerCase() === country.toLowerCase());
                  if (!countryDetails) return;
                  const stateDetails = countryDetails.states.find(x => x.name.toLowerCase() == state.toLowerCase());
                  // Update donor info with state & country
                  setDonorInfo((prevInfo) => {
                    const newInfo = new DonorInfo({
                      ...prevInfo,
                      country: countryDetails.id,
                      countryDetails: countryDetails,
                      state: stateDetails?.id || "",
                      city: city
                    });
                    newInfo.validate(true);
                    return newInfo;
                  });
                } else {
                  console.error("Geocoding failed:", data.status);
                }
              })
              .catch((err) => console.error("Error fetching location:", err));
          }
          break;
      }
      return updatedInfo;
    });
  };


  // const handleEmailBlur = () => {
  //   setTouchedFields(prev => ({ ...prev, email: true }));
  //   if (donorInfo.email.trim() && !isValidEmail(donorInfo.email)) {
  //     setEmailError(true);
  //   } else {
  //     setEmailError(false);
  //   }
  // };

  // const handlePhoneBlur = () => {
  //   setTouchedFields(prev => ({ ...prev, phone: true }));
  //   if (donorInfo.phone.trim() !== '') {
  //     // For US numbers, check if it's exactly 10 digits when formatted
  //     if (donorInfo.country === 'United States') {
  //       const digitsOnly = donorInfo.phone.replace(/\D/g, '');
  //       setPhoneError(digitsOnly.length > 0 && digitsOnly.length !== 10);
  //     } else {
  //       // For non-US, just check it's not empty if entered
  //       setPhoneError(false);
  //     }
  //   } else {
  //     setPhoneError(false);
  //   }
  // };

  const isStepValid = () => {
    // Validation disabled for faster testing
    // const hasValidName = donorInfo.firstName.trim() !== '' &&
    //                     donorInfo.lastName.trim() !== '' &&
    //                     donorInfo.email.trim() !== '' &&
    //                     isValidEmail(donorInfo.email);

    // Mobile phone validation - optional but if entered, must be valid 10-digit US number
    let phoneValid = true;
    if (donorInfo.phone.trim() !== "") {
      const newErrors: InputErrorModel<DonorInfo>[] = [];
      // For US numbers, check if it's exactly 10 digits when formatted
      CoreInputModelUtil.validatePhoneNumber(
        "phone",
        donorInfo.phone,
        newErrors
      );
      phoneValid = newErrors.length === 0;
    }

    if (!phoneValid) {
      setPhoneError(true);
    }
    // return hasValidName && phoneValid && !phoneError;
    return true; // Validation disabled for testing
  };

  const countryStates = (
    countriesWithState.find((c) => c.id === donorInfo.country)?.states || []
  )
    .map((state) => {
      return {
        label: state.name,
        value: state.id,
      };
    })
    .sort((a, b) => a.label.localeCompare(b.label));
  return (
    <DonorFormContainer>
      <NameRow>
        <FormGroup>
          <FormInput
            type="text"
            placeholder="First Name"
            value={donorInfo.firstName}
            onChange={(e) => updateDonorInfo("firstName", e.target.value)}
            hasError={donorInfo.hasError("firstName")}
          />
        </FormGroup>
        <FormGroup>
          <FormInput
            type="text"
            placeholder="Last Name"
            value={donorInfo.lastName}
            onChange={(e) => updateDonorInfo("lastName", e.target.value)}
            hasError={donorInfo.hasError("lastName")}
          />
        </FormGroup>
      </NameRow>

      <FullWidthFormGroup>
        <FormInput
          type="text"
          placeholder="Street Address"
          value={donorInfo.streetAddress}
          onChange={(e) => updateDonorInfo("streetAddress", e.target.value)}
          hasError={donorInfo.hasError("streetAddress")}
        />
      </FullWidthFormGroup>

      <ZipCityRow>
        <FormGroup>
          <FormInput
            type="text"
            placeholder="Zip"
            value={donorInfo.zip}
            onChange={(e) => updateDonorInfo("zip", e.target.value)}
            hasError={donorInfo.hasError("zip")}
          />
        </FormGroup>
        <FormGroup>
          <FormInput
            type="text"
            placeholder="City"
            value={donorInfo.city}
            onChange={(e) => updateDonorInfo("city", e.target.value)}
            hasError={donorInfo.hasError("city")}
          />
        </FormGroup>
      </ZipCityRow>

      <StateCountryRow>
        <FormGroup tabIndex={0}>
          <SingleSelect
            selectedKey={donorInfo.state}
            className="default-padding-search-dpdn single-select-donor-input donation-capture-select"
            options={[countryStates || []]}
            nameKeys={["label"]}
            valueKeys={["value"]}
            hasError={donorInfo.hasError("state")}
            placeholder="Select State"
            searchable={true}
            errorMessage="Select State"
            onChange={(option: any) => updateDonorInfo("state", option.value)}
          />
          {/* <FormInput
            type="text"
            placeholder="State"
            value={donorInfo.state}
            onChange={(e) => updateDonorInfo('state', e.target.value)}
          /> */}
        </FormGroup>
        {/* <FormGroup>
          <CountrySelect
            value={donorInfo.country}
            disabled
          >
            <option value="United States">🇺🇸 United States</option>
          </CountrySelect>
        </FormGroup> */}
        <FormGroup className="donation-country-dropdown">
          <CountryDropdown
            className="country-dropdown country-dropdown-expanded"
            selectedCountry={
              countriesWithState.find((c) => c.id === donorInfo.country) ||
              new CountryWithStateModel()
            }
            // selectedCountry={new CountryWithStateModel()}
            error={donorInfo.hasError("country")}
            errorMessage={"Please select country"}
            onCountryChange={(id: string) => {
              updateDonorInfo("country", id);
              updateDonorInfo(
                "countryDetails",
                countriesWithState.find((c) => c.id === id) ||
                new CountryWithStateModel()
              );
              if (id != donorInfo.country) {
                updateDonorInfo("state", "");
              }
            }}
            availableCountries={countriesWithState}
          />
        </FormGroup>
      </StateCountryRow>

      <FullWidthFormGroup>
        <FormInput
          type="email"
          placeholder="Email"
          value={donorInfo.email}
          onChange={(e) => updateDonorInfo("email", e.target.value)}
          //onBlur={handleEmailBlur}
          hasError={donorInfo.hasError("email")}
        />
      </FullWidthFormGroup>

      {/* <FullWidthFormGroup>
        <PhoneInputContainer>
          <PhonePrefix>+1</PhonePrefix>
          <PhoneInput
            type="tel"
            placeholder="Mobile Phone"
            value={donorInfo.phone}
            onChange={(e) => {
              // Handle raw input to allow proper clearing
              const rawValue = e.target.value;
              updateDonorInfo('phone', rawValue);
            }}
            onBlur={handlePhoneBlur}
            hasError={phoneError}
            onKeyDown={(e) => {
              // Allow backspace to clear formatted characters
              if (e.key === 'Backspace') {
                const input = e.target as HTMLInputElement;
                const { selectionStart, selectionEnd, value } = input;

                // If user has selected text, let default behavior handle it
                if (selectionStart !== selectionEnd) {
                  return;
                }

                // If backspace at a dash position, skip the dash
                if (selectionStart && (value[selectionStart - 1] === '-')) {
                  e.preventDefault();
                  const newValue = value.slice(0, selectionStart - 2) + value.slice(selectionStart);
                  updateDonorInfo('phone', newValue);
                }
              }
            }}
          />
        </PhoneInputContainer>
      </FullWidthFormGroup> */}

      <FullWidthFormGroup className="donation-country-dropdown">
        <CountryPhoneDropdown
          selectedCountry={
            countriesWithState.find((c) => c.id === donorInfo.phoneCountryId) ||
            new CountryWithStateModel()
          }
          error={
            donorInfo.hasError("phoneCountryId") || donorInfo.hasError("phone")
          }
          errorMessage={"Please select country and enter valid phone number"}
          onCountryChange={(id: string) => {
            updateDonorInfo("phoneCountryId", id);
            updateDonorInfo(
              "phoneCountryCode",
              countriesWithState.find((c) => c.id === id)?.code?.toString() ||
              ""
            );
          }}
          phoneNumber={donorInfo.phone}
          onPhoneNumberChange={(number: string) =>
            updateDonorInfo("phone", number)
          }
          availableCountries={countriesWithState}
        />
      </FullWidthFormGroup>
      <CheckboxContainer>
        <CheckboxItem>
          <Checkbox
            type="checkbox"
            checked={donorInfo.receiveNotifications}
            onChange={(e) =>
              updateDonorInfo("receiveNotifications", e.target.checked)
            }
          />
          Get Notifications
        </CheckboxItem>

        <CheckboxItem>
          <Checkbox
            type="checkbox"
            checked={donorInfo.isAnonymous}
            onChange={(e) => updateDonorInfo("isAnonymous", e.target.checked)}
          />
          Anonymous Donation
        </CheckboxItem>
      </CheckboxContainer>

      {donorInfo.receiveNotifications && (
        <DisclaimerText>
          I agree to opt-in to receive text and email communication. I may
          opt-out at any time. For text messages reply STOP to end. Message and
          data rates may apply.
        </DisclaimerText>
      )}

      {!donorInfo.receiveNotifications && <LineBreakSpacer />}
    </DonorFormContainer>
  );
};
